/**
 * Workspace screens (dashboard, billing, settings, team): option lists that mirror backend
 * enums/limits (`app/core/constants/workspace_settings.py`, billing schemas, DEC-010).
 */
import { SECURE_LAUNCH_PANEL } from "./launch";

/** Dashboard / analytics period switch (`AnalyticsRange` minus `custom`). */
export const DASHBOARD_RANGES = ["7d", "30d", "90d"] as const;
export type DashboardRange = (typeof DASHBOARD_RANGES)[number];

/** Canonical webhook events (DEC-010: scope M10.4 + lead.updated, message.created; backend `WebhookEvent`). */
export const WEBHOOK_EVENTS = [
  "conversation.created",
  "conversation.handoff",
  "lead.created",
  "lead.updated",
  "message.created",
  "source.processed",
  "source.failed",
  "feedback.negative",
  "conversation.assigned",
  "conversation.status_changed",
  "booking.requested",
  "booking.confirmed",
  "booking.declined",
  "booking.cancelled",
  "conversation.rated",
  "sla.breached",
  // Service Desk (M27 S1, SD-S1-23): the 13 ticket events, then the client side. Mirrors `WebhookEvent`.
  "ticket.created",
  "ticket.updated",
  "ticket.state_changed",
  "ticket.assigned",
  "ticket.message_created",
  "ticket.note_added",
  "ticket.sla_warning",
  "ticket.sla_breached",
  "ticket.resolved",
  "ticket.reopened",
  "ticket.closed",
  "ticket.merged",
  "ticket.csat_received",
  "organisation.created",
  "organisation.updated",
  "contact.created",
  "contact.updated",
  "contact.verified",
  // After-sales (M27 S3, SD-S3-09, scope 26): assets, visits, RMAs and contracts.
  "asset.created",
  "asset.updated",
  "visit.scheduled",
  "visit.status_changed",
  "rma.status_changed",
  "contract.expiring",
  // Orders and quotes (M27 S2, SD-S2-16, scope 26).
  "order.created",
  "order.status_changed",
  "order.approval_requested",
  "order.approval_decided",
  "order.sync_failed",
  "quote.status_changed",
] as const;
export type WebhookEventKey = (typeof WEBHOOK_EVENTS)[number];

/** Body an endpoint receives (backend `WebhookFormat`): Sagot's signed JSON, or a readable chat message. */
export const WEBHOOK_FORMATS = ["sagot", "discord", "slack"] as const;
export type WebhookFormatKey = (typeof WEBHOOK_FORMATS)[number];
export const DEFAULT_WEBHOOK_FORMAT: WebhookFormatKey = "sagot";

/** When an endpoint is called (backend `WebhookDeliveryMode`, DEC-083). */
export const WEBHOOK_DELIVERY_MODES = ["events", "filtered", "workflows"] as const;
export type WebhookDeliveryMode = (typeof WEBHOOK_DELIVERY_MODES)[number];
/** Filters of a `filtered` endpoint (an empty list = no limit). */
export const WEBHOOK_FILTER_CHANNELS = ["widget", "webapp", "mobile", "messenger", "email", "api"] as const;
export const WEBHOOK_FILTER_ROLES = ["visitor", "assistant", "staff"] as const;

/** Message text and feedback comments in an event are redacted, then cut to this (backend `WEBHOOK_TEXT_MAX_CHARS`). */
export const WEBHOOK_TEXT_MAX_CHARS = 1000;

/** Signature header sent with every webhook delivery (HMAC-SHA256 of the body). */
export const WEBHOOK_SIGNATURE_HEADER = "Sagot-Signature";
export const WEBHOOK_DELIVERIES_LIMIT = 20;

/** Scopes of an API key (backend `ApiScope`); the Service Desk ones (SD-S1-27) come last. */
export const API_KEY_SCOPES = [
  "chat:write",
  "knowledge:write",
  "analytics:read",
  "conversations:read",
  // Secure launch and customer context (M29, backend `ApiScope`; endpoints behind the `secure_launch` flag).
  "launch:write",
  "customers:read",
  "customers:write",
  "workflows:read",
  "tickets:read",
  "tickets:write",
  "organisations:read",
  "organisations:write",
  "catalogue:read",
  "orders:read",
  "orders:write",
] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

/** Expiry choices in the create-key dialog; `null` = never expires. */
export const API_KEY_EXPIRY_DAYS = { never: null, year: 365, quarter: 90 } as const;
export type ApiKeyExpiry = keyof typeof API_KEY_EXPIRY_DAYS;
export const API_KEY_DEFAULT_EXPIRY: ApiKeyExpiry = "year";
export const API_KEY_NAME_MAX_LENGTH = 120;

/** Key hardening (M29 SEC-005; backend `API_KEY_IP_RANGES_MAX`, `API_KEY_ROTATION_OVERLAP_S`, `API_SIGNATURE_HEADER`). */
export const API_KEY_IP_RANGES_MAX = 20;
/** In-place rotation keeps the previous secret working this many hours. */
export const API_KEY_ROTATION_OVERLAP_HOURS = 24;
export const API_SIGNATURE_HEADER = "Sagot-Request-Signature";
/** Scopes that belong to Secure launch: offered only to workspaces that have it, and the keys the scoped-key card lists. */
export const API_KEY_LAUNCH_SCOPES = ["launch:write", "customers:read", "customers:write"] as const;

export const SESSION_TIMEOUTS = ["30m", "8h", "7d", "30d"] as const;
export type SessionTimeoutKey = (typeof SESSION_TIMEOUTS)[number];

/** Workspace conversation retention (backend 30–365 days) and attachment retention options. */
export const RETENTION_DAY_OPTIONS = [30, 90, 365] as const;
export const ATTACHMENT_RETENTION_DAY_OPTIONS = [7, 30, 90] as const;

/** PII redaction toggles (backend `PiiKind`). */
export const PII_KINDS = ["mobile", "email", "national_id", "card_bank", "address", "gov_id"] as const;
export type PiiKindKey = (typeof PII_KINDS)[number];

export const WORKSPACE_LANGUAGES = ["en", "fil"] as const;

/** Logo upload (backend `LOGO_MIME_EXTENSIONS`, `LOGO_MAX_BYTES`; no SVG per DEC-024). */
export const LOGO_ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
export const LOGO_MAX_BYTES = 2 * 1_048_576;

export const MAX_ALLOWED_EMAIL_DOMAINS = 20;
export const MAX_IP_ALLOWLIST = 100;
export const AUDIT_PAGE_SIZE = 50;

/** Settings panels (`/w/:ws/settings/:panel`); `domains` + `notifications` added per DEC-010, `attributes` + `sla` per DEC-086, `secure-launch` per M29 (shown only where the platform flag is on). */
export const SETTINGS_PANELS = [
  "org",
  "profile",
  "api",
  "webhooks",
  "integrations",
  SECURE_LAUNCH_PANEL,
  "security",
  "privacy",
  "audit",
  "notifications",
  "domains",
  "attributes",
  "sla",
] as const;
export type SettingsPanel = (typeof SETTINGS_PANELS)[number];
/** Panels every member reaches (their own account); the rest need `settings.view`. */
export const PERSONAL_SETTINGS_PANELS: readonly SettingsPanel[] = ["profile", "notifications"];

/** Billing: payment method kinds, add-on bounds and query flags set by the PSP return URLs. */
export const PAYMENT_METHOD_TYPES = ["gcash", "maya", "card", "bank"] as const;
export type PaymentMethodKind = (typeof PAYMENT_METHOD_TYPES)[number];
export const SEAT_ADDON_MIN = 1;
export const SEAT_ADDON_MAX = 10;
export const AUTO_TOPUP_MAX_PACKS = 10;
export const CHECKOUT_RESULT_PARAM = "checkout";
/** `?addon=<key>` on the Billing page opens that add-on's purchase dialog (the knowledge "Add storage" link). */
export const ADDON_PARAM = "addon";
/** Payment id on the PSP return URL (`?checkout=success&ref=<payment_id>`) → confirm it. */
export const CHECKOUT_REF_PARAM = "ref";
/** Saved-method setup return (`?setup=success&gateway=stripe&ref=…`). */
export const SETUP_RESULT_PARAM = "setup";
export const SETUP_GATEWAY_PARAM = "gateway";
/** sessionStorage key holding the started setup id across the gateway redirect. */
export const SETUP_STORAGE_KEY = "sg-billing-setup";
/** Billing currencies (DEC-042) and the gateways that collect each (payer picks for USD). */
export const BILLING_CURRENCIES = ["PHP", "USD"] as const;
export type BillingCurrencyCode = (typeof BILLING_CURRENCIES)[number];
export const PAYMENT_GATEWAYS = ["paymongo", "stripe", "paypal"] as const;
export type PaymentGatewayKey = (typeof PAYMENT_GATEWAYS)[number];
/** Mode a gateway runs in, as reported by `GET /v1/billing/config` (DEC-065). */
export const GATEWAY_MODES = ["live", "test", "mock"] as const;
export type GatewayModeKey = (typeof GATEWAY_MODES)[number];
/** Gateways whose methods are saved through a hosted setup flow (PayMongo tokenises in-app). */
export const HOSTED_SETUP_GATEWAYS = ["stripe", "paypal"] as const;
/** MockPayments (dev) returns this console path instead of a PayMongo checkout page. */
export const DEV_CHECKOUT_PATH_PATTERN = /\/billing\/dev-checkout\/([0-9a-f-]{36})\/?$/i;
export const BILLING_INVOICES_PAGE_SIZE = 50;

/** Add-ons a workspace can buy itself, in the order the Billing page lists them (the white-label mobile setup is done by CRUD.IT). */
export const SELF_SERVE_ADDONS = ["message_pack", "extra_agent", "extra_seat", "remove_branding", "knowledge_storage_1gb"] as const;
/** Add-ons offered only on some plans and only while the catalogue lists them (an admin switches them on): hidden on the Billing page otherwise. */
export const CATALOGUE_GATED_ADDONS: readonly string[] = ["knowledge_storage_1gb"];
/** Units one workspace can hold in total, mirror of `ADDON_MAX_UNITS` (backend `plans.py`); beyond it the Institution plan. */
export const ADDON_MAX_UNITS: Readonly<Record<string, number>> = { knowledge_storage_1gb: 10 };
/** One unit of `knowledge_storage_1gb` in MB, mirror of `KNOWLEDGE_STORAGE_ADDON_MB`. */
export const KNOWLEDGE_STORAGE_ADDON_MB = 1024;
/** `details.reason` of a 402 `plan_limit_reached` that says the knowledge storage meter is full (backend `KNOWLEDGE_STORAGE_FULL_REASON`). */
export const PLAN_LIMIT_REASON_KEY = "reason";
export const KNOWLEDGE_STORAGE_FULL_REASON = "knowledge_storage_full";
export const SALES_EMAIL = "contact@cruditsolutions.com";
/** PayMongo client-side tokenization (card data never reaches the Sagot API). */
export const PAYMONGO_API_URL = "https://api.paymongo.com/v1";
export const TIN_PATTERN = /^\d{3}-?\d{3}-?\d{3}(-?\d{3,5})?$/;
/** Credits meter turns warning at this share of the allowance (projection over 100% also warns). */
export const USAGE_WARN_RATIO = 0.8;
/** Sidebar "Messages this month" meter: stale after a minute (refetched on tab focus), polled every five. */
export const SIDEBAR_USAGE_STALE_MS = 60_000;
export const SIDEBAR_USAGE_REFETCH_MS = 5 * 60_000;
